// 消息管理相关功能
import { atom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';
import i18n from '../../../lib/i18n';
import { fetchApi } from '../../../lib/apiClient';
import { NotificationService, NotificationOptions } from '../../../services/NotificationService';
import { 
  MessagesResponse, 
  Message, 
  INITIAL_PLACEHOLDER_ID 
} from './types';
import { 
  selectedConversationIdAtom, 
  messagesAtom, 
  loadingMoreAtom, 
  hasMoreMessagesAtom, 
  oldestMessageIdAtom,
  pendingMessagesStorage
} from './baseAtoms';
import { fetchConversationsAtom } from './conversationAtoms';

/**
 * 加载更多历史消息
 */
export const loadMoreMessagesAtom = atom(
  null,
  async (get, set) => {
    const conversationId = get(selectedConversationIdAtom);
    const oldestMessageId = get(oldestMessageIdAtom);
    const hasMoreMessages = get(hasMoreMessagesAtom);

    if (get(loadingMoreAtom)) return;
    if (!conversationId || !oldestMessageId || !hasMoreMessages) return;

    try {
      set(loadingMoreAtom, true);

      // 构建URL查询参数 - Path only
      const path = `/chat/messages?conversationId=${conversationId}&limit=20&beforeId=${oldestMessageId}`;

      const response = await fetchApi<MessagesResponse>(path, {
        method: 'GET'
      });

      // 合并新加载的消息
      if (get(selectedConversationIdAtom) === conversationId) {
        set(messagesAtom, (prevMessages) => [...response.messages, ...prevMessages]);
        set(hasMoreMessagesAtom, response.hasMore);

        // 更新最早消息ID
        if (response.messages.length > 0) {
          set(oldestMessageIdAtom, response.oldestId);
        }
      }

      return response;
    } catch (error) {
      console.error('Failed to load more messages:', error);
      throw error;
    } finally {
      set(loadingMoreAtom, false);
    }
  }
);

/**
 * 处理所有待处理消息
 */
export const handlePendingMessagesAtom = atom(
  null,
  async (get, set) => {
    const pendingMessages = get(pendingMessagesStorage);
    const currentConvId = get(selectedConversationIdAtom);

    // 检查当前会话是否有待处理消息
    const pendingForCurrentConv = Object.values(pendingMessages).filter(
      pending => pending.conversationId === currentConvId
    );

    if (pendingForCurrentConv.length > 0) {
      // 有待处理消息，重新加载会话以显示最新状态
      if (currentConvId && currentConvId !== 'new') {
        set(fetchConversationsAtom);
      }
    }

    // 清理已完成或过期的待处理消息
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000); // 1小时前

    const expiredIds = Object.entries(pendingMessages)
      .filter(([_, msg]) => new Date(msg.timestamp).getTime() < oneHourAgo)
      .map(([id, _]) => id);

    if (expiredIds.length > 0) {
      set(pendingMessagesStorage, prev => {
        const newPending = { ...prev };
        expiredIds.forEach(id => delete newPending[id]);
        return newPending;
      });
    }
  }
);

/**
 * 创建用户消息和初始AI占位符消息
 */
export const createMessagesHelper = (text: string, mediaId?: string): { userMessage: Message, initialPlaceholderMessage: Message } => {
  const userTimestamp = new Date().toISOString();
  // 创建用户消息
  const userMessage: Message = {
    id: `temp-user-${Date.now()}`,
    role: 'user',
    content: [
      {
        type: 'text',
        text: text.trim()
      }
    ],
    timestamp: userTimestamp,
    mediaIds: mediaId ? [mediaId] : undefined
  };

  // --- CHANGE: Generate temporary final ID immediately --- 
  const tempFinalId = `final-ai-${Date.now()}`;
  const stableKey = uuidv4(); // Generate stable key

  const initialPlaceholderMessage: Message = {
    // id: INITIAL_PLACEHOLDER_ID, // No longer use constant placeholder ID
    id: tempFinalId, // Use the temporary final ID from the start
    role: 'assistant',
    content: [
      {
        type: 'text',
        text: i18n.t('v3/chat:messages.thinking', '正在思考中...')
      }
    ],
    timestamp: new Date(new Date(userTimestamp).getTime() + 1).toISOString(),
    metadata: {
      status: 'loading',
      streaming: true,
      stableKey: stableKey, // Use the generated stable key
      displayType: 'placeholder' // Indicate it's still functionally a placeholder initially
    }
  };

  return { userMessage, initialPlaceholderMessage };
};

/**
 * 发送通知
 */
export const sendChatNotification = async (conversationId: string, aiMessage: Message) => {
  try {
    // 检查用户是否还在聊天页面
    const isDocumentVisible = !document.hidden;
    const isOnChatPage = window.location.pathname.includes('/chat');

    // 如果用户不在聊天页面或文档不可见，发送通知
    if (!isDocumentVisible || !isOnChatPage) {
      const notificationService = NotificationService.getInstance();
      const permissionStatus = await notificationService.checkPermission();

      if (permissionStatus === 'granted') {
        // 确保安全地获取消息内容
        const messageContent = aiMessage.content[0];
        const aiContent = typeof messageContent === 'string'
          ? messageContent
          : (messageContent as any).text || '';

        const displayContent = aiContent.length > 100
          ? aiContent.substring(0, 97) + '...'
          : aiContent;

        // 修复通知选项类型问题
        const notificationOptions: NotificationOptions = {
          id: `chat-${Date.now()}`,
          title: '新消息',
          body: displayContent,
          sound: true,
          extra: {
            conversationId: conversationId,
            messageId: aiMessage.id,
            type: 'chat_reply'
          }
        };

        await notificationService.scheduleLocalNotification(notificationOptions);
      }
    }
  } catch (notifError) {
    console.error('Failed to send notification:', notifError);
  }
};
